// OnboardingDetailPage-part2.tsx
// PART 2: Main Component Logic, State, and Handlers

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import adminOnboardingService from '../services/adminOnboardingService';
import { OnboardingDetails } from '../types/onboarding';

// Custom hook for onboarding detail page logic
export const useOnboardingDetailLogic = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  // State
  const [onboarding, setOnboarding] = useState<OnboardingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([1, 2, 3, 4, 5, 6, 7, 8]));

  // Action modals
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showStepActionModal, setShowStepActionModal] = useState(false);
  const [selectedStep, setSelectedStep] = useState<string>('');
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [actionNotes, setActionNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // ✅ Toast notification instead of alert()
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    if (userId) {
      loadOnboardingDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadOnboardingDetails = async (isRefresh = false) => {
    try {
      // ✅ Only show full-page loading spinner on initial load, NOT on refresh
      // This prevents the page from unmounting/remounting and losing scroll position
      if (!isRefresh) {
        setLoading(true);
      }
      setError('');
      
      
      const response = await adminOnboardingService.getOnboardingDetails(userId!);
      
      
      setOnboarding(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load onboarding details');
    } finally {
      if (!isRefresh) {
        setLoading(false);
      }
    }
  };

  const toggleStep = (stepNumber: number) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepNumber)) {
      newExpanded.delete(stepNumber);
    } else {
      newExpanded.add(stepNumber);
    }
    setExpandedSteps(newExpanded);
  };

  const handleStepAction = (stepName: string, action: 'approve' | 'reject') => {
    setSelectedStep(stepName);
    setActionType(action);
    setShowStepActionModal(true);
  };

  const executeStepAction = async () => {
    if (!selectedStep) return;

    try {
      setProcessing(true);
      
      if (actionType === 'approve') {
        await adminOnboardingService.approveStep(userId!, selectedStep, actionNotes);
      } else {
        if (!rejectionReason.trim()) {
          showToast('Please provide a rejection reason', 'error');
          return;
        }
        await adminOnboardingService.rejectStep(userId!, selectedStep, rejectionReason, actionNotes);
      }

      // ✅ Refresh data WITHOUT full-page spinner (preserves scroll position)
      await loadOnboardingDetails(true);
      
      // ✅ Show inline toast instead of blocking alert
      showToast(
        `Step "${selectedStep}" ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`,
        'success'
      );
      
      setShowStepActionModal(false);
      setSelectedStep('');
      setActionNotes('');
      setRejectionReason('');
    } catch (err: any) {
      showToast(err.message || 'Action failed', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleFinalApproval = async () => {
    try {
      setProcessing(true);
      await adminOnboardingService.approveOnboarding(userId!, actionNotes);
      await loadOnboardingDetails(true);
      setShowApprovalModal(false);
      setActionNotes('');
      showToast('Driver onboarding approved successfully', 'success');
    } catch (err: any) {
      showToast(err.message || 'Approval failed', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleFinalRejection = async () => {
    if (!rejectionReason.trim()) {
      showToast('Please provide a rejection reason', 'error');
      return;
    }

    try {
      setProcessing(true);
      await adminOnboardingService.rejectOnboarding(userId!, rejectionReason, actionNotes);
      await loadOnboardingDetails(true);
      setShowRejectionModal(false);
      setRejectionReason('');
      setActionNotes('');
      showToast('Driver onboarding rejected', 'success');
    } catch (err: any) {
      showToast(err.message || 'Rejection failed', 'error');
    } finally {
      setProcessing(false);
    }
  };

  return {
    userId,
    navigate,
    onboarding,
    loading,
    error,
    expandedSteps,
    showApprovalModal,
    setShowApprovalModal,
    showRejectionModal,
    setShowRejectionModal,
    showStepActionModal,
    setShowStepActionModal,
    selectedStep,
    actionType,
    actionNotes,
    setActionNotes,
    rejectionReason,
    setRejectionReason,
    processing,
    toast,
    showToast,
    toggleStep,
    handleStepAction,
    executeStepAction,
    handleFinalApproval,
    handleFinalRejection,
    loadOnboardingDetails
  };
};